import moment from 'moment';
import { BalanceSheetAllow, FinancialStatementPeriodFullWrite, IncomeStatementAllow } from 'api';
import { LAST_TWELVE_MONTHS, LTM, NEXT_TWELVE_MONTHS } from 'common/constants/financials';
import { SelectValueViewer } from 'components';
import { GridSelect } from 'components/FeaturedSpreadsheet/components';
import {
  ADJUSTED_EBITDA_OPTION,
  COMPANY,
  EBITDA_SELECTION_ALIAS,
  EBITDA_SELECTION_OPTIONS,
  EBITDA_SELECTION_ROW_TRANSFORMER_MAP,
  ENTERPRISE_VALUE_COLUMN_LEGEND,
  FIRST_FORWARD_COLUMN_LEGEND,
  FIRST_FORWARD_ID,
  FIRST_NON_FORWARD_COLUMN_LEGEND,
  FIRST_NON_FORWARD_ID,
  FIRST_PERIOD_EBITDA_SELECTION_CELL,
  FIRST_PERIOD_EBITDA_SELECTION_COLUMN,
  FIRST_PERIOD_SELECTION_CELL,
  FORWARD_PRICE_TO_EARNINGS_PERIOD,
  FORWARD_PRICE_TO_TANGIBLE_PERIOD,
  LTM_EBITDA,
  ltmColumnLetters,
  MAIN_COLUMNS_IDS,
  MAP_EBITDA_SUFFIX_VALUE,
  MARKET_CAP_COLUMN_LEGEND,
  MEAN_ALIAS,
  MEDIAN_ALIAS,
  NTM_EBITDA,
  ntmColumnLetters,
  PERCENTILE_SELECTION_A_ALIAS,
  PERCENTILE_SELECTION_B_ALIAS,
  PRICE_TO_EARNINGS_PERIOD,
  PRICE_TO_TANGIBLE_PERIOD,
  SECOND_FORWARD_COLUMN_LEGEND,
  SECOND_FORWARD_ID,
  SECOND_NON_FORWARD_ID,
  SECOND_PERIOD_EBITDA_SELECTION_CELL,
  SECOND_PERIOD_EBITDA_SELECTION_COLUMN,
  SECOND_PERIOD_SELECTION_CELL,
  SELECTED_MULTIPLE_VALUE,
  SELECTION,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/constants';
import { percentileChange, selectionChange } from 'pages/ValuationsAllocation/util';
import { getBooleanValue, getNumberValue, MOMENT_DEFAULT_DATE_FORMAT } from 'utilities';
import { ebitdaSelectionPropMap, legendCalendarYearPropMap, legendColumnIdMap, resolveEbitdaSelectionLabel } from './auxMaps';
import { getFinancialPeriodValue } from './utilities';
import {
  GPCAfterCellChanged,
  GPCChange,
  ReverseParsedValuationApproachGPC,
  StringMapperObject,
  ValuationApproachGPCCell,
  ValuationApproachGPCCells,
} from '../../types';

const getChangedColumnIndicator = ({
  cell,
  isEvaluatingEquityValue,
}: {
  cell: ValuationApproachGPCCell;
  isEvaluatingEquityValue: boolean;
}) => {
  if (isEvaluatingEquityValue) {
    return legendColumnIdMap[cell.alias];
  }

  if (cell.alias === LAST_TWELVE_MONTHS || cell.columnId === SECOND_NON_FORWARD_ID) {
    return ltmColumnLetters;
  }

  if (cell.alias === NEXT_TWELVE_MONTHS || cell.columnId === SECOND_FORWARD_ID) {
    return ntmColumnLetters;
  }

  return [];
};

const getComparisonPropToRead = ({
  legend,
  isEvaluatingEquityValue,
  ebitdaSelection,
}: {
  legend: string;
  isEvaluatingEquityValue: boolean;
  ebitdaSelection: string;
}) => {
  if (!isEvaluatingEquityValue) {
    return [FIRST_NON_FORWARD_COLUMN_LEGEND, FIRST_FORWARD_COLUMN_LEGEND].includes(legend)
      ? 'revenue'
      : (ebitdaSelectionPropMap[ebitdaSelection] as string);
  }
  return legendCalendarYearPropMap[legend] as string;
};

// Pick the selection that matches the column being recalculated.
const getEbitdaSelectionLabelForLegend = ({
  legend,
  cell,
  value,
  cells,
}: {
  legend: string;
  cell: ValuationApproachGPCCell;
  value: string | number | null | undefined;
  cells: ValuationApproachGPCCells;
}) => {
  if (cell.alias === EBITDA_SELECTION_ALIAS) {
    if (legend === FIRST_PERIOD_EBITDA_SELECTION_COLUMN && cell.columnId === SECOND_NON_FORWARD_ID) {
      return value as string;
    }
    if (legend === SECOND_PERIOD_EBITDA_SELECTION_COLUMN && cell.columnId === SECOND_FORWARD_ID) {
      return value as string;
    }
  }
  if (legend === SECOND_PERIOD_EBITDA_SELECTION_COLUMN) {
    return cells[SECOND_PERIOD_EBITDA_SELECTION_CELL]?.value as string;
  }
  return cells[FIRST_PERIOD_EBITDA_SELECTION_CELL]?.value as string;
};

const afterPeriodSelectorCellChanged = ({
  valuations_approach_gpc,
  cell,
  financialsPeriods,
  value,
  changes,
  cells,
}: {
  valuations_approach_gpc: ReverseParsedValuationApproachGPC;
  cell: ValuationApproachGPCCell;
  financialsPeriods: Partial<FinancialStatementPeriodFullWrite>[];
  value: string | number | null | undefined;
  changes: GPCChange[];
  cells: ValuationApproachGPCCells;
}) => {
  let comparisonRow = 3;
  let periodId = null;
  if (
    [
      LAST_TWELVE_MONTHS,
      NEXT_TWELVE_MONTHS,
      PRICE_TO_EARNINGS_PERIOD,
      FORWARD_PRICE_TO_EARNINGS_PERIOD,
      PRICE_TO_TANGIBLE_PERIOD,
      FORWARD_PRICE_TO_TANGIBLE_PERIOD,
    ].includes(cell.alias!)
  ) {
    periodId = value as string | number;
  } else if (cell.alias === EBITDA_SELECTION_ALIAS) {
    if (cell.columnId === SECOND_NON_FORWARD_ID) {
      periodId = cells[FIRST_PERIOD_SELECTION_CELL]?.value as string | number;
    } else if (cell.columnId === SECOND_FORWARD_ID) {
      periodId = cells[SECOND_PERIOD_SELECTION_CELL]?.value as string | number;
    }
  }
  const { is_evaluating_equity_value, gpc_comparison: comparisons } = valuations_approach_gpc;
  const isEvaluatingEquityValue = getBooleanValue(is_evaluating_equity_value);
  const columnLettersToParse = getChangedColumnIndicator({ cell, isEvaluatingEquityValue });
  const numeratorColumnLegend = isEvaluatingEquityValue ? MARKET_CAP_COLUMN_LEGEND : ENTERPRISE_VALUE_COLUMN_LEGEND;
  const sortedComparisons = comparisons?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) ?? [];
  (sortedComparisons ?? []).forEach(comparison => {
    if (comparison.calendar_years_financials) {
      (columnLettersToParse as string[]).forEach(legend => {
        const type = getComparisonPropToRead({
          legend,
          isEvaluatingEquityValue,
          ebitdaSelection: getEbitdaSelectionLabelForLegend({ legend, cell, value, cells }),
        });
        let financialPeriodValue = getFinancialPeriodValue({
          comparison,
          periods: financialsPeriods,
          periodId: periodId as string | number,
          type,
          isEvaluatingEquityValue,
        });

        financialPeriodValue = getNumberValue(financialPeriodValue as string);
        const expr = financialPeriodValue ? `=${numeratorColumnLegend}${comparisonRow}/${financialPeriodValue}` : 0;

        changes.push({
          cell: cells[legend + comparisonRow],
          value: expr,
        });
      });

      comparisonRow += 1;
    }
  });

  const companyCells = Object.values(cells)
    .filter(cell => cell.alias === COMPANY && MAIN_COLUMNS_IDS.includes(cell.columnId as string))
    .filter(({ columnLegend }) => columnLettersToParse.includes(columnLegend as string));

  const selectedPeriod = financialsPeriods.find(p => p?.id?.toString() === periodId?.toString());
  const incomeStatement = (selectedPeriod?.income_statement as IncomeStatementAllow) ?? {};
  const {
    total_revenue: revenue = '0',
    net_income: netIncome = '0',
  } = incomeStatement;
  const isNtmContext = (columnLettersToParse as string[]).includes(SECOND_FORWARD_COLUMN_LEGEND);
  let ebitdaSelectionRaw: string | number | null | undefined;
  if (cell.alias === EBITDA_SELECTION_ALIAS) {
    ebitdaSelectionRaw = value;
  } else if (isNtmContext) {
    ebitdaSelectionRaw = cells[SECOND_PERIOD_EBITDA_SELECTION_CELL]?.value as string | number | null | undefined;
  } else {
    ebitdaSelectionRaw = cells[FIRST_PERIOD_EBITDA_SELECTION_CELL]?.value as string | number | null | undefined;
  }
  const ebitdaSelectionLabel = resolveEbitdaSelectionLabel(ebitdaSelectionRaw);
  // Header selection drives whether the subject company row reads GAAP EBITDA or adjusted EBITDA.
  const ebitdaField = EBITDA_SELECTION_ROW_TRANSFORMER_MAP[
    ebitdaSelectionLabel as keyof typeof EBITDA_SELECTION_ROW_TRANSFORMER_MAP
  ] ?? MAP_EBITDA_SUFFIX_VALUE;
  const ebitdaValue = (incomeStatement[ebitdaField as keyof IncomeStatementAllow] ?? '0') as string;
  const { tangible_book_value: tangibleBookValue = '0' } = (selectedPeriod?.balance_sheet as BalanceSheetAllow) ?? {};
  const firstAndThirdColumnFinancialProp = isEvaluatingEquityValue ? tangibleBookValue : revenue;
  const secondAndFourthColumnFinancialProp = isEvaluatingEquityValue ? netIncome : ebitdaValue;
  const newPeriodValue: StringMapperObject = {
    [FIRST_NON_FORWARD_ID]: firstAndThirdColumnFinancialProp as string,
    [FIRST_FORWARD_ID]: firstAndThirdColumnFinancialProp as string,
    [SECOND_NON_FORWARD_ID]: secondAndFourthColumnFinancialProp as string,
    [SECOND_FORWARD_ID]: secondAndFourthColumnFinancialProp as string,
  };

  companyCells.forEach(cell => {
    changes.push({
      cell,
      value: newPeriodValue[cell.columnId as string] as string,
    });
  });
};

const afterCellChanged: GPCAfterCellChanged = (changes, cells, rowConfig, tableData) => {
  const { approach, financialsPeriods, marketDate } = tableData;
  const { valuations_approach_gpc } = approach;
  const { use_multiple_premium_discount, is_evaluating_equity_value: isEvaluatingEquityValue } =
    valuations_approach_gpc;

  changes.forEach(change => {
    const { cell, value } = change;

    const relatedFinancialPeriod = financialsPeriods.find(p => Number(p.id) === Number(value));
    // For the purposes of allowing the user to select an EBITDA option or not, a period
    // is considered historical if it is before the market date or if it is the LTM period
    if (relatedFinancialPeriod && [LAST_TWELVE_MONTHS, NEXT_TWELVE_MONTHS].includes(cell.alias!)) {
      const isHistoricalPeriod =
        moment(relatedFinancialPeriod?.statement_date).isBefore(moment(marketDate, MOMENT_DEFAULT_DATE_FORMAT)) ||
        relatedFinancialPeriod?.period_type === LTM;
      const options = EBITDA_SELECTION_OPTIONS(isHistoricalPeriod);
      const ebitdaSelectionCell =
        cell.columnId === FIRST_NON_FORWARD_ID
          ? cells[FIRST_PERIOD_EBITDA_SELECTION_CELL]
          : cells[SECOND_PERIOD_EBITDA_SELECTION_CELL];
      ebitdaSelectionCell.dataEditor = (props: any) =>
        GridSelect({
          ...props,
          enumerated: true,
          optionsTextAlign: 'left',
          options,
        });
      ebitdaSelectionCell.valueViewer = (props: any) =>
        SelectValueViewer({
          ...props,
          options,
          useLabel: true,
        });
    }

    switch (cell.alias) {
      // if the alias is to the percentile header, then we need to update the selection cells
      // if the alias if for the selection cells, then we need to update the formula for the applied multiple cell

      case PERCENTILE_SELECTION_A_ALIAS:
      case PERCENTILE_SELECTION_B_ALIAS:
        percentileChange(cell, value, cells, changes, use_multiple_premium_discount);
        break;

      case SELECTION:
        selectionChange(cells, changes, cell, value, use_multiple_premium_discount);
        break;

      case COMPANY:
        // need to adjust these two conditions
        if (cell.columnId === SECOND_NON_FORWARD_ID && !isEvaluatingEquityValue) {
          valuations_approach_gpc.use_adjusted_LTM_ebitda =
            Number(change.value) !== Number(cell?.sheet?.tableData.financials[LTM_EBITDA] ?? 0);
        }
        if (cell.columnId === SECOND_FORWARD_ID && !isEvaluatingEquityValue) {
          valuations_approach_gpc.use_adjusted_NTM_ebitda =
            Number(change.value) !== Number(cell?.sheet?.tableData.financials[NTM_EBITDA] ?? 0);
        }
        break;

      case MEDIAN_ALIAS:
      case MEAN_ALIAS:
      case SELECTED_MULTIPLE_VALUE:
        changes.push({
          cell,
          value: cell.expr,
        });
        break;
      case LAST_TWELVE_MONTHS:
      case NEXT_TWELVE_MONTHS:
      case PRICE_TO_EARNINGS_PERIOD:
      case PRICE_TO_TANGIBLE_PERIOD:
      case FORWARD_PRICE_TO_EARNINGS_PERIOD:
      case FORWARD_PRICE_TO_TANGIBLE_PERIOD:
      case EBITDA_SELECTION_ALIAS:
        // Persist adjusted-EBITDA intent on the approach when the header dropdown changes, not only on manual company-row edits.
        if (!isEvaluatingEquityValue) {
          if (cell.columnId === SECOND_NON_FORWARD_ID) {
            valuations_approach_gpc.use_adjusted_LTM_ebitda = value === ADJUSTED_EBITDA_OPTION;
          }
          if (cell.columnId === SECOND_FORWARD_ID) {
            valuations_approach_gpc.use_adjusted_NTM_ebitda = value === ADJUSTED_EBITDA_OPTION;
          }
        }
        afterPeriodSelectorCellChanged({
          valuations_approach_gpc,
          cell,
          financialsPeriods,
          value,
          changes,
          cells,
        });
        break;
      default:
        break;
    }
  });

  return changes;
};

export default afterCellChanged;
